import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Yup from '../../../utils/yup';
import { OpeningHours, Surgery } from '../../../types/surgery';
import { openingHoursErrors } from '../../../utils/openingHours';

export interface PublicProfileUpdate {
  list_on_directory: boolean;
  public_phone: string | null;
  public_email: string | null;
  website: string | null;
  hours: OpeningHours | null;
}

const PHONE_PATTERN = /^\+?[0-9 ()-]+$/;
const PHONE_MAX_LENGTH = 30;
const WEBSITE_MAX_LENGTH = 2048;

const PHONE_MESSAGE = 'Use digits, spaces, brackets, dashes and an optional leading +';
const EMAIL_MESSAGE = 'Enter a valid email address';
const WEBSITE_MESSAGE = 'Enter the full address, starting with https://';

const isWebAddress = (value: string) => {
  try {
    return ['http:', 'https:'].includes(new URL(value).protocol);
  } catch {
    return false;
  }
};

const optionalText = () =>
  Yup.string()
    .nullable()
    .transform((value) => (typeof value === 'string' ? value.trim() || null : value));

const schema = Yup.object().shape({
  list_on_directory: Yup.boolean(),
  public_phone: optionalText()
    .max(PHONE_MAX_LENGTH, PHONE_MESSAGE)
    .matches(PHONE_PATTERN, { message: PHONE_MESSAGE, excludeEmptyString: true })
    .test('has-digit', PHONE_MESSAGE, (value) => !value || /\d/.test(value)),
  public_email: optionalText().email(EMAIL_MESSAGE),
  website: optionalText()
    .max(WEBSITE_MAX_LENGTH, WEBSITE_MESSAGE)
    .test('web-address', WEBSITE_MESSAGE, (value) => !value || isWebAddress(value)),
  hours: Yup.mixed().test(
    'opening-hours',
    'Fix the opening hours',
    (hours) => Object.keys(openingHoursErrors(hours)).length === 0,
  ),
});

const usePublicProfileForm = (surgery: Surgery) =>
  useForm<PublicProfileUpdate>({
    mode: 'onChange',
    defaultValues: {
      list_on_directory: Boolean(surgery.list_on_directory),
      public_phone: surgery.public_phone || '',
      public_email: surgery.public_email || '',
      website: surgery.website || '',
      hours: surgery.hours || null,
    },
    resolver: yupResolver(schema),
  });

export default usePublicProfileForm;
